import { lazy, Suspense, useContext, useMemo } from 'react';

import { Container, Nav, Navbar } from 'react-bootstrap';
import { useNavigate } from 'react-router';

import { ReactComponent as FullLogoSvg } from '@assets/images/full_logo.svg';
import { ReactComponent as NateraLogoSvg } from '@assets/images/natera/natera-web-logo.svg';
import PartnerBanner from '@components/RequisitionCreate/PartnerBanner/PartnerBanner';
import useAuth from '@hooks/useAuth';
import useViewportSizing from '@hooks/useViewportSizing';
import { useOpenReplayDebugToggle } from '@hooks/useOpenReplayDebugToggle';
import { useSettings } from '@hooks/useSettings';
import { ROUTE_PATHS } from '@router/paths';
import { Context } from '@store/store';

import { IGroupSetting } from './GroupSettings';

import './HeaderNav.scss';

const LazyMobileDropdown = lazy(() => import('./MobileDropdown'));
const LazyDesktopNav = lazy(() => import('./DesktopNav'));
const LazyAccessBanner = lazy(() => import('./AccessBanner'));

const Header = () => {
  const { onLogin, isLoggedIn } = useAuth();
  const [state] = useContext(Context);
  const { isNatera } = useSettings();

  const navigate = useNavigate();
  const { isMobile } = useViewportSizing();
  const { isEnabled: isDebugToggleEnabled, toggle: toggleDebug, isDebugSupported } = useOpenReplayDebugToggle();

  const hasAdminClinic = state.adminClinics.length > 0;
  const hasOrderingClinic = state.orderingProviderClinics.length > 0;

  const userSettings: IGroupSetting[] = useMemo(() => {
    const accountSettings: IGroupSetting = {
      title: isMobile ? 'Account Settings' : '',
      settings: [],
    };

    if (hasOrderingClinic) {
      accountSettings.settings.push({
        href: ROUTE_PATHS.MY_DELEGATES,
        label: 'My Delegates',
      });
    }

    const results = [accountSettings];

    if (hasAdminClinic) {
      results.push({
        title: 'Admin Settings',
        settings: [
          {
            label: 'Manage',
            href: ROUTE_PATHS.MANAGE_USERS,
          },
        ],
      });
    }

    // Add debug toggle only in non-prod environments
    if (isDebugSupported) {
      results.push({
        title: '[DEV] Debug Settings',
        settings: [
          {
            href: '#',
            label: `${isDebugToggleEnabled ? '☑' : '☐'} Show OpenReplay Sanitization`,
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              toggleDebug?.();
            },
          },
        ],
      });
    }

    return results.filter(item => item.settings.length > 0);
  }, [hasAdminClinic, hasOrderingClinic, isMobile, isDebugToggleEnabled, toggleDebug, isDebugSupported]);

  const canShowAccessBanner = isLoggedIn && (!isMobile || state.hasPartnershipOrdering);

  const navigateToHome = () => {
    navigate(ROUTE_PATHS.ROOT);
  };

  return (
    <Suspense>
      <header
        // eslint-disable-next-line max-len
        className={`fixed-top ${canShowAccessBanner ? 'has-access-banner' : ''} ${state.hasPartnershipOrdering ? 'has-partnership-ordering' : ''}`}
      >
        {canShowAccessBanner && (
          <LazyAccessBanner
            hasPartnershipOrdering={state.hasPartnershipOrdering}
            userDisplayName={state.userProfile?.fullName}
            userSettings={userSettings}
          />
        )}

        <Navbar
          expand='md'
          className={`bg-white ${isNatera ? 'nat-nav' : 'border-bottom border-cyan border-2 box-shadow-100'} py-md-3 main-navbar`}
        >
          <Container fluid='xxl' className='d-flex justify-content-between align-items-center'>
            <Navbar.Brand onClick={navigateToHome} className='p-0'>
              {isNatera ? (
                <a href='https://www.natera.com/' target='_blank' rel='noopener noreferrer'>
                  <NateraLogoSvg height={isMobile ? 30 : 40} />
                </a>
              ) : (
                <FullLogoSvg height={isMobile ? 30 : 40} />
              )}
            </Navbar.Brand>

            {isMobile && isLoggedIn && <Navbar.Toggle className='toggle-btn' />}

            {!isLoggedIn && (
              <Nav.Link
                className={isNatera ? 'natera-nav-link' : ''}
                data-testid='login-link'
                onClick={() => onLogin()}
              >
                Login
              </Nav.Link>
            )}

            {isLoggedIn && (
              <Navbar.Collapse className='mb-n2 mt-4 mb-md-0 mt-md-0'>
                {isMobile ? (
                  <LazyMobileDropdown userDisplayName={state.userProfile?.fullName} userSettings={userSettings} />
                ) : (
                  <LazyDesktopNav />
                )}
              </Navbar.Collapse>
            )}
          </Container>
        </Navbar>
      </header>

      <PartnerBanner />
    </Suspense>
  );
};

export default Header;
